import { supabase } from '@/lib/supabase/client';
import { buildNurtureUpdatePayload } from '@/lib/prospect-nurture/helpers';
import type { BlueprintLead, BlueprintLeadStatus, BlueprintLeadUpdate } from '@/types/blueprint-leads';

export const blueprintLeadsService = {
  async getAll(): Promise<BlueprintLead[]> {
    // Throw on error (don't collapse failures into an empty list) so consumers can show an error state.
    const { data, error } = await supabase
      .rpc('list_blueprint_leads_redacted', { p_limit: 500 });

    if (error) {
      console.error('Error fetching Blueprint leads:', error.message);
      throw new Error(`Failed to fetch Blueprint leads: ${error.message}`);
    }

    return (data as BlueprintLead[]) || [];
  },

  async getById(id: string): Promise<BlueprintLead | null> {
    try {
      const { data, error } = await supabase
        .rpc('get_blueprint_lead_redacted', { p_lead_id: id });

      if (error) {
        console.error('Error fetching Blueprint lead:', error);
        return null;
      }

      return (data as BlueprintLead) || null;
    } catch (error) {
      console.error('Error fetching Blueprint lead:', error);
      return null;
    }
  },

  async update(id: string, data: BlueprintLeadUpdate): Promise<BlueprintLead> {
    try {
      const updateData: Record<string, unknown> = {};
      if (data.status !== undefined) updateData.status = data.status;
      if (data.owner_user_id !== undefined) updateData.owner_user_id = data.owner_user_id;
      if (data.owner_name !== undefined) updateData.owner_name = data.owner_name?.trim() || null;
      if (data.notes !== undefined) updateData.notes = data.notes?.trim() || null;
      Object.assign(updateData, buildNurtureUpdatePayload(data));

      const { data: updatedLead, error } = await supabase.rpc('update_blueprint_lead_redacted', {
        p_lead_id: id,
        p_changes: updateData,
      });

      if (error) {
        console.error('Error updating Blueprint lead:', error);
        throw new Error(`Failed to update Blueprint lead: ${error.message}`);
      }

      return updatedLead as BlueprintLead;
    } catch (error) {
      console.error('Error updating Blueprint lead:', error);
      throw error;
    }
  },

  async updateStatus(id: string, status: BlueprintLeadStatus): Promise<BlueprintLead> {
    return this.update(id, { status });
  },

  async assignOwner(id: string, ownerUserId: string | null, ownerName: string | null): Promise<BlueprintLead> {
    return this.update(id, {
      owner_user_id: ownerUserId,
      owner_name: ownerName,
    });
  },

  async updateNotes(id: string, notes: string | null): Promise<BlueprintLead> {
    return this.update(id, { notes });
  },
};
